import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { MaskLine, Reveal, FadeIn, Overline } from "@/components/motion";
import { Marquee } from "@/components/Layout";
import { VALORI, WA_URL } from "@/lib/site";

const PAZIENTI = [
  {
    title: "Adulti e giovani adulti sotto pressione",
    text: "Giornate sotto pressione, in famiglia e al lavoro. Il mal di testa torna, il sonno non ristora, l'umore ne risente. E spesso c'è anche il timore di prendere farmaci che agiscono sul cervello.",
    link: "Richiedi una visita →",
    href: "/prenota",
  },
  {
    title: "Persone anziane, e chi se ne prende cura",
    text: "Difficoltà a camminare, instabilità, tremore. Oppure episodi di confusione e disorientamento. Quasi sempre è la famiglia ad accorgersene per prima: puoi scriverci tu, per lui o per lei.",
    link: "Scrivici per un tuo familiare →",
    href: WA_URL,
    external: true,
  },
  {
    title: "Giovani con fascicolazioni muscolari",
    text: "Un muscolo che si muove da solo, un «guizzo», poi un altro. E la paura di una malattia grave. È uno dei motivi più frequenti per cui i giovani adulti chiedono un accertamento.",
    link: "Scopri l'elettromiografia →",
    href: "/prestazioni#emg",
  },
  {
    title: "Adulti con vuoti di memoria",
    text: "Capita anche a chi è giovane, spesso per stress o ansia. Merita comunque di essere capito con calma: tra i primi controlli c'è quasi sempre anche lo studio del sonno.",
    link: "Richiedi una visita →",
    href: "/prenota",
  },
  {
    title: "Persone con cefalea",
    text: "C'è chi ha un mal di testa «serio» per la prima volta e si spaventa. E c'è chi convive con la cefalea da anni, senza essere mai stato gestito in maniera corretta.",
    link: "Richiedi una visita →",
    href: "/prenota",
  },
];

const PICCOLO_CARDS = [
  {
    title: "Un ambiente familiare",
    text: "Un ambulatorio accogliente e tranquillo, dove non ti senti un numero.",
  },
  {
    title: "Comunicazione diretta",
    text: "Parli direttamente con lo studio, e arrivare ai professionisti per un dubbio o un chiarimento è semplice.",
  },
  {
    title: "Tempo dedicato",
    text: "Uno studio attento e completo, con i tempi che il tuo caso richiede.",
  },
  {
    title: "Referti in tempi rapidi",
    text: "Ogni esame ha il suo tempo di refertazione, dichiarato in anticipo.",
  },
];

const IMPEGNO_PUNTI = [
  "Offrirti un riferimento solido a cui appoggiarti, per restituirti serenità.",
  "Illustrarti chiaramente il quadro clinico, con i possibili scenari e le conseguenze, per restituirti consapevolezza.",
  "Intervenire dove è possibile, per restituirti salute, o almeno qualità di vita.",
];

function PageHero() {
  return (
    <section className="bg-crema pt-40 pb-24 lg:pt-52 lg:pb-32 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -left-40 top-10 w-[420px] h-[420px] rounded-full border border-indaco/15" />
        <div className="absolute -left-16 top-40 w-[420px] h-[420px] rounded-full border border-arancio/20" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <FadeIn>
          <Overline>Il Centro</Overline>
        </FadeIn>
        <h1 className="mt-8 font-sora font-extrabold tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-ink" data-testid="centro-title">
          <MaskLine delay={0.15}>Qui il tempo dedicato a te</MaskLine>
          <MaskLine delay={0.3}>
            <span className="text-indaco">non è un'eccezione.</span>
          </MaskLine>
        </h1>
        <FadeIn delay={0.6}>
          <p className="mt-8 max-w-2xl text-viole text-lg leading-relaxed">
            Il paziente neurologico ha bisogno prima di tempo e attenzione, poi di esami e soluzioni.
            Synapses è nato per invertire quest'ordine.
          </p>
        </FadeIn>
        <FadeIn delay={0.75}>
          <Link
            to="/team"
            className="mt-8 inline-flex items-center gap-2 text-indaco font-semibold text-[15px] md:text-base group"
            data-testid="centro-hero-team"
          >
            Conosci chi ti seguirà
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeIn>
      </div>
    </section>
  );
}

function Origine() {
  return (
    <section className="bg-crema-warm py-24 lg:py-36" data-testid="origine-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Reveal>
            <Overline>Perché è nato</Overline>
          </Reveal>
        </div>
        <div className="lg:col-span-8 space-y-8">
          <Reveal>
            <h2 className="font-sora font-bold tracking-tighter text-3xl md:text-5xl text-ink leading-tight">
              Da venti minuti codificati
              <br />
              al tempo che serve.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-lg text-ink/80 leading-relaxed max-w-2xl">
              Il centro nasce dall'esperienza del fondatore, il Dott. Enrico Cavallo, durante gli anni
              di specializzazione: le visite ambulatoriali per pazienti esterni erano codificate in
              venti minuti massimo, poi ridotti a quindici. Un tempo inadeguato anche solo per avviare
              un buon rapporto terapeutico.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="text-lg text-ink/80 leading-relaxed max-w-2xl">
              Synapses non è un ospedale, non è una clinica di lusso, non è un ambulatorio low cost,
              non è una catena di montaggio, non è un luogo freddo e impersonale. È un piccolo
              ambulatorio altamente attrezzato, dove il tempo dedicato al paziente è la base da cui
              parte tutto il resto.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Impegno() {
  return (
    <section className="bg-crema py-24 lg:py-36" data-testid="impegno-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Reveal>
            <Overline>Il nostro impegno</Overline>
          </Reveal>
        </div>
        <div className="lg:col-span-8">
          <Reveal>
            <h2 className="font-sora font-bold tracking-tighter text-3xl md:text-5xl text-ink leading-tight">
              Cosa ci impegniamo a fare, dalla prima visita in poi.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-lg text-viole leading-relaxed max-w-2xl">
              Chi arriva qui spesso è confuso, perché non sa bene cosa ha e a chi rivolgersi.
              Ed è spaventato, perché non conosce l'evoluzione del suo disturbo. Il nostro impegno è questo.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 space-y-6 max-w-2xl">
              {IMPEGNO_PUNTI.map((p) => (
                <div key={p} className="flex gap-5">
                  <span className="mt-1 h-px w-8 bg-arancio shrink-0 mt-3" />
                  <p className="text-ink/80 leading-relaxed">{p}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <Link
              to="/prenota"
              className="mt-12 inline-flex items-center gap-3 rounded-full bg-indaco px-8 py-4 text-[15px] md:text-base font-semibold text-crema transition-colors duration-300 hover:bg-indaco-deep group"
              data-testid="impegno-cta"
            >
              Raccontaci cosa ti succede
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <p className="mt-5 text-[15px] md:text-base text-viole">Ti rispondiamo noi entro 24 ore, senza impegno.</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Valori() {
  return (
    <section className="bg-indaco-deep text-crema py-24 lg:py-36" data-testid="valori-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Overline light>I nostri valori</Overline>
        </Reveal>
        <div className="mt-14 divide-y divide-crema/10">
          {VALORI.map((v, i) => (
            <Reveal key={v.num} delay={0.08 * i}>
              <div className="group grid md:grid-cols-12 gap-6 py-12 items-start">
                <span className="md:col-span-2 font-sora text-[15px] md:text-base text-arancio font-semibold tracking-widest">{v.num}</span>
                <h3 className="md:col-span-4 font-sora font-bold tracking-tighter text-2xl md:text-4xl transition-colors duration-300 group-hover:text-arancio">
                  {v.title}
                </h3>
                <p className="md:col-span-6 text-crema/65 leading-relaxed max-w-xl">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pazienti() {
  return (
    <section className="bg-crema-warm py-24 lg:py-36" data-testid="pazienti-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Overline>Chi arriva da noi</Overline>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-sora font-bold tracking-tighter text-3xl md:text-5xl text-ink max-w-3xl">
            Le situazioni che vediamo più spesso.
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-6 text-viole text-lg leading-relaxed max-w-2xl">
            Nella maggior parte dei casi chi ci contatta cerca un medico che abbia la pazienza
            di ascoltare e che resti disponibile nel tempo. Attenzione e presenza, prima ancora
            di una soluzione immediata.
          </p>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PAZIENTI.map((p, i) => (
            <Reveal key={p.title} delay={0.07 * i}>
              <div className="bg-crema border border-ink/5 rounded-lg p-8 h-full flex flex-col transition-colors duration-300 hover:border-indaco/30" data-testid={`paziente-card-${i}`}>
                <h3 className="font-sora font-semibold tracking-tight text-lg text-ink">{p.title}</h3>
                <p className="mt-3 text-viole text-[15px] md:text-base leading-relaxed flex-1">{p.text}</p>
                {p.external ? (
                  <a
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center gap-2 text-indaco font-semibold text-[15px] md:text-base"
                    data-testid={`paziente-link-${i}`}
                  >
                    {p.link}
                  </a>
                ) : (
                  <Link
                    to={p.href}
                    className="mt-6 inline-flex items-center gap-2 text-indaco font-semibold text-[15px] md:text-base"
                    data-testid={`paziente-link-${i}`}
                  >
                    {p.link}
                  </Link>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CentroPiccolo() {
  return (
    <section className="bg-crema py-24 lg:py-32" data-testid="centro-piccolo">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Overline>Perché un centro piccolo</Overline>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-sora font-bold tracking-tighter text-3xl md:text-5xl text-ink max-w-3xl">
            Cosa cambia, rispetto a una struttura grande.
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PICCOLO_CARDS.map((c, i) => (
            <Reveal key={c.title} delay={0.08 * i}>
              <div className="group border border-ink/10 rounded-lg p-8 h-full transition-colors duration-300 hover:border-indaco/40 hover:bg-crema-warm" data-testid={`piccolo-card-${i}`}>
                <h3 className="font-sora font-bold tracking-tight text-xl text-ink transition-colors duration-300 group-hover:text-indaco">
                  {c.title}
                </h3>
                <p className="mt-3 text-viole text-[15px] md:text-base leading-relaxed">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaFinale() {
  return (
    <section className="bg-indaco-deep text-crema py-24 lg:py-32" data-testid="centro-cta">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <Reveal>
          <h2 className="font-sora font-bold tracking-tighter text-3xl md:text-5xl">
            Il primo passo è raccontarci cosa ti succede.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-6 text-crema/70 text-lg">
            Anche in due righe. Ti rispondiamo noi entro 24 ore, senza impegno.
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/prenota"
              className="group inline-flex items-center gap-3 rounded-full bg-crema px-8 py-4 text-[15px] md:text-base font-semibold text-ink transition-colors duration-300 hover:bg-arancio hover:text-crema"
              data-testid="centro-cta-prenota"
            >
              Richiedi un appuntamento →
            </Link>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-full border border-crema/30 px-8 py-4 text-[15px] md:text-base font-semibold text-crema transition-colors duration-300 hover:border-crema"
              data-testid="centro-cta-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              Chiedi su WhatsApp
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.35}>
          <p className="mt-8 text-[15px] md:text-base text-crema/60">
            Vuoi prima conoscere chi ti seguirà?{" "}
            <Link to="/team" className="text-crema font-semibold underline underline-offset-4" data-testid="centro-cta-team">
              Scopri il team →
            </Link>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export default function Centro() {
  return (
    <>
      <PageHero />
      <Marquee items={["Ascolto ed empatia", "Collaborazione", "Competenza specialistica", "Tempi rapidi"]} />
      <Origine />
      <Impegno />
      <Valori />
      <Pazienti />
      <CentroPiccolo />
      <CtaFinale />
    </>
  );
}
